import * as React from 'react'
import { useNavigate } from 'react-router-dom'
import { Pencil, Plus, RotateCcw, Trash2 } from 'lucide-react'
import type { TenantRoleDto } from '@lib/dto/tenant-role.dto'
import { areasFor, grantsSummary, REACH_LABEL } from '@/components/roles/roleText'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { ConfirmDialog } from '@/components/ui/confirm-dialog'
import { PageContainer } from '@/components/ui/page-container'
import { PageHeader } from '@/components/ui/page-header'
import { RowActions, type RowAction } from '@/components/ui/row-actions'
import { ErrorState, ListSkeleton } from '@/components/ui/states'
import { Table, TableActionsCell, TableActionsHead, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { requestErrorMessage } from '@/helpers/apiClient'
import { useOrganization } from '@/hooks/useOrganization'
import { useDeleteTenantRole, useResetTenantRole, useTenantRoles } from '@/hooks/useTenantRoles'

/**
 * The company's roles (docs/custom-roles.spec.md): Organization admin, which is
 * fixed, then the built-in Venue manager, then the company's own. Organization
 * admins only.
 */
export function RolesPage() {
  const navigate = useNavigate()
  const rolesQuery = useTenantRoles()
  const features = useOrganization().data?.features ?? []
  const areas = areasFor(features)
  const reset = useResetTenantRole()
  const remove = useDeleteTenantRole()
  const [resetting, setResetting] = React.useState<TenantRoleDto | null>(null)
  const [deleting, setDeleting] = React.useState<TenantRoleDto | null>(null)

  function actionsFor(role: TenantRoleDto): RowAction[] {
    const edit: RowAction = { label: 'Edit role', icon: Pencil, priority: true, onSelect: () => navigate(`/roles/${role.id}`) }
    if (role.builtIn !== null) {
      return [edit, { label: 'Reset to defaults', icon: RotateCcw, onSelect: () => setResetting(role) }]
    }
    return [
      edit,
      {
        label: 'Delete role',
        icon: Trash2,
        destructive: true,
        onSelect: () => setDeleting(role),
        disabled: role.people > 0,
        disabledReason: role.people > 0 ? 'Move the people who hold it to another role first.' : undefined,
      },
    ]
  }

  function renderContent() {
    if (rolesQuery.isPending) return <ListSkeleton rows={3} />
    if (rolesQuery.isError) {
      return <ErrorState message={requestErrorMessage(rolesQuery.error)} onRetry={() => void rolesQuery.refetch()} />
    }
    const { organizationAdmins, roles } = rolesQuery.data
    return (
      <Table
        heading={`${String(roles.length + 1)} roles`}
        actions={
          <Button type="button" size="sm" onClick={() => navigate('/roles/new')}>
            <Plus className="h-4 w-4" /> New role
          </Button>
        }
      >
        <TableHeader>
          <TableHead>Role</TableHead>
          <TableHead>Works at</TableHead>
          <TableHead className="text-right">People</TableHead>
          <TableActionsHead />
        </TableHeader>
        <TableBody>
          <TableRow>
            <TableCell>
              <RoleName name="Organization admin" badge="Fixed" />
              <p className="m-0 text-small text-ink-muted">Everything, including roles and billing.</p>
            </TableCell>
            <TableCell className="text-ink-muted">{REACH_LABEL.company}</TableCell>
            <TableCell className="text-right font-mono tabular-nums text-ink-muted">{organizationAdmins}</TableCell>
            <TableActionsCell />
          </TableRow>
          {roles.map((role) => (
            <TableRow
              key={role.id}
              className="cursor-pointer"
              onClick={() => navigate(`/roles/${role.id}`)}
              onKeyDown={(e) => {
                if (e.target !== e.currentTarget) return
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  navigate(`/roles/${role.id}`)
                }
              }}
              tabIndex={0}
            >
              <TableCell className="max-w-0 w-1/2">
                <RoleName name={role.name} badge={role.builtIn === null ? null : 'Built in'} />
                <p className="m-0 truncate text-small text-ink-muted">{grantsSummary(role.grants, areas)}</p>
              </TableCell>
              <TableCell className="text-ink-muted">{REACH_LABEL[role.reach]}</TableCell>
              <TableCell className="text-right font-mono tabular-nums text-ink-muted">{role.people}</TableCell>
              {/* The menu handles its own clicks; they mustn't also open the row. */}
              <TableActionsCell onClick={(e) => e.stopPropagation()}>
                <RowActions label={role.name} actions={actionsFor(role)} />
              </TableActionsCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    )
  }

  return (
    <PageContainer width="wide" className="space-y-6">
      <PageHeader
        title="Roles"
        description="What each kind of staff member can do. Organization admins can always do everything."
      />
      {renderContent()}
      <ConfirmDialog
        open={resetting !== null}
        onOpenChange={(open) => {
          if (!open) setResetting(null)
        }}
        title={`Reset ${resetting?.name ?? 'this role'} to its defaults?`}
        description="It goes back to what a venue manager can do at their assigned venues. Its name stays as it is."
        confirmLabel="Reset role"
        busy={reset.isPending}
        error={reset.isError ? requestErrorMessage(reset.error) : null}
        onConfirm={() => {
          if (resetting === null) return
          reset.mutate(resetting.id, { onSuccess: () => setResetting(null) })
        }}
      />
      <ConfirmDialog
        open={deleting !== null}
        onOpenChange={(open) => {
          if (!open) setDeleting(null)
        }}
        title={`Delete ${deleting?.name ?? 'this role'}?`}
        description="Nobody holds it. A deleted role can’t be brought back."
        confirmLabel="Delete role"
        destructive
        busy={remove.isPending}
        error={remove.isError ? requestErrorMessage(remove.error) : null}
        onConfirm={() => {
          if (deleting === null) return
          remove.mutate(deleting.id, { onSuccess: () => setDeleting(null) })
        }}
      />
    </PageContainer>
  )
}

function RoleName({ name, badge }: { name: string; badge: string | null }) {
  return (
    <span className="flex items-center gap-2">
      <span className="min-w-0 truncate text-sm font-medium text-ink">{name}</span>
      {badge === null ? null : <Badge>{badge}</Badge>}
    </span>
  )
}
